// src/pages/Settings.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDate, formatNumber, formatPercentage, formatDuration } from '../utils/formatters';
import { useNavigate, useParams } from 'react-router-dom';


import { z } from 'zod';
import {
  User,
  Mail,
  Globe,
  Shield,
  Bell,
  Palette,
  Key,
  Trash2,
  Save,
  Crown,
  CreditCard,
  Download,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { apiClient } from '@/app-lib/api-client';

import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { useFeatureAccess } from '@/components/auth/ProtectedRoute';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';
import toast from 'react-hot-toast';

interface BillingHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: string;
  description: string;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
}

interface SubscriptionDetails {
  tier: string;
  status: string;
  billing_interval?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  next_billing_date?: string;
  latest_invoice?: any;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
}

const profileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  timezone: z.string().min(1, 'Please select a timezone'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(8, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain uppercase, lowercase, number and special character'),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'account', label: 'Account', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'data', label: 'Data & Privacy', icon: Download },
];

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { userProfile, updateUserProfile, signOut } = useAuthStore();
  const { theme, toggleTheme, subscriptionStatus } = useAppStore();
  const { subscription, hasFeature } = useFeatureAccess();

  const [activeTab, setActiveTab] = useState(tab || 'profile');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);


  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: userProfile?.name || '',
      email: userProfile?.email || '',
      timezone: userProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  // Update activeTab when URL parameter changes
  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (userProfile) {
      profileForm.reset({
        name: userProfile.name,
        email: userProfile.email,
        timezone: userProfile.timezone,
      });
    }
  }, [userProfile, profileForm]);

  useEffect(() => {
    if (subscription?.tier !== 'free') {
      loadBillingData();
    } else {
      setBillingLoading(false);
    }
  }, [subscription]);

  const loadBillingData = async () => {
    try {
      setBillingLoading(true);

      // Fetch subscription details
      const detailsResponse = await apiClient.get('/api/billing/subscription');
      setSubscriptionDetails(detailsResponse.data);

      // Fetch billing history
      const historyResponse = await apiClient.get('/api/billing/history?limit=10');
      setBillingHistory(historyResponse.data);

    } catch (error: any) {
      console.error('Error loading billing data:', error);
      if (error?.response?.status !== 404) {
        toast.error('Failed to load billing information');
      }
    } finally {
      setBillingLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setUpdatingPayment(true);
      const response = await apiClient.post('/api/billing/update-payment-method');

      if (response.data.update_url) {
        toast.success('Redirecting to payment update...');
        window.location.href = response.data.update_url;
      } else {
        throw new Error('No update URL received');
      }
    } catch (error: any) {
      console.error('Error updating payment method:', error);
      const errorMessage = error?.response?.data?.detail?.message ||
        error?.message ||
        'Failed to update payment method';
      toast.error(errorMessage);
      setUpdatingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    try {
      setCancellingSubscription(true);
      await apiClient.post('/api/billing/cancel-subscription');
      toast.success('Subscription cancelled. Access continues until period end.');
      await loadBillingData();
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      const errorMessage = error?.response?.data?.detail?.message ||
        error?.message ||
        'Failed to cancel subscription';
      toast.error(errorMessage);
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await apiClient.post('/api/billing/reactivate-subscription');
      toast.success('Subscription reactivated successfully!');
      await loadBillingData();
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      const errorMessage = error?.response?.data?.detail?.message ||
        error?.message ||
        'Failed to reactivate subscription';
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const handleProfileSubmit = async (data: ProfileForm) => {
    try {
      setIsLoading(true);
      await updateUserProfile(data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (_data: PasswordForm) => {
    try {
      setIsLoading(true);
      // TODO: Implement password change with Cognito
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      passwordForm.reset();
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true);
      // TODO: Implement account deletion
      toast.success('Account deletion request submitted');
      await signOut();
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleExportData = async () => {
    try {
      setIsLoading(true);
      // TODO: Implement data export
      toast.success('Data export started. You will receive an email when ready.');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Failed to start data export');
    } finally {
      setIsLoading(false);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Profile Information
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Update your account details and preferences.
        </p>
      </div>

      <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            placeholder="Your full name"
            leftIcon={<User className="w-4 h-4" />}
            error={profileForm.formState.errors.name?.message}
            {...profileForm.register('name')}
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            leftIcon={<Mail className="w-4 h-4" />}
            error={profileForm.formState.errors.email?.message}
            {...profileForm.register('email')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Globe className="w-4 h-4 inline mr-2" />
            Timezone
          </label>
          <select
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            {...profileForm.register('timezone')}
          >
            {[
              'UTC',
              'America/New_York',
              'America/Chicago',
              'America/Denver',
              'America/Los_Angeles',
              'Europe/London',
              'Europe/Paris',
              'Europe/Berlin',
              'Asia/Tokyo',
              'Asia/Shanghai',
              'Asia/Kolkata',
              'Australia/Sydney'
            ].map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
          {profileForm.formState.errors.timezone && (
            <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.timezone.message}</p>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            loading={isLoading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );

  const renderAccountTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Account Security
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Manage your account security settings.
        </p>
      </div>

      {/* Password Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Password</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Last changed 3 months ago
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowPasswordForm(true)}
            leftIcon={<Key className="w-4 h-4" />}
          >
            Change Password
          </Button>
        </div>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              Two-Factor Authentication
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add an extra layer of security to your account
            </p>
          </div>
          {hasFeature('advanced_security') ? (
            <Button variant="outline">
              Enable 2FA
            </Button>
          ) : (
            <UpgradePrompt
              trigger={
                <Button variant="outline" disabled>
                  <Crown className="w-4 h-4 mr-2" />
                  Pro Feature
                </Button>
              }
              feature="Advanced Security"
            />
          )}
        </div>
      </Card>

      {/* Active Sessions */}
      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Active Sessions
        </h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
            <div>
              <p className="font-medium text-sm">Current session</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Chrome on Windows • Mumbai, India • Active now
              </p>
            </div>
            <span className="text-xs text-green-600 dark:text-green-400">Current</span>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-sm">Safari on iPhone</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Mumbai, India • 2 hours ago
              </p>
            </div>
            <Button size="sm" variant="outline">
              Revoke
            </Button>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="p-6 border-red-200 dark:border-red-800">
        <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
          Danger Zone
        </h4>
        <p className="text-sm text-red-700 dark:text-red-300 mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <Button
          variant="danger"
          onClick={() => setShowDeleteDialog(true)}
          leftIcon={<Trash2 className="w-4 h-4" />}
        >
          Delete Account
        </Button>
      </Card>

      {/* Password Change Modal */}
      <Modal
        isOpen={showPasswordForm}
        onClose={() => setShowPasswordForm(false)}
        title="Change Password"
      >
        <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            placeholder="Enter current password"
            error={passwordForm.formState.errors.currentPassword?.message}
            {...passwordForm.register('currentPassword')}
          />

          <Input
            label="New Password"
            type="password"
            placeholder="Enter new password"
            error={passwordForm.formState.errors.newPassword?.message}
            {...passwordForm.register('newPassword')}
          />

          <Input
            label="Confirm New Password"
            type="password"
            placeholder="Confirm new password"
            error={passwordForm.formState.errors.confirmPassword?.message}
            {...passwordForm.register('confirmPassword')}
          />

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPasswordForm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              className="flex-1"
            >
              Change Password
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Account Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed."
        confirmLabel="Delete Account"
        variant="danger"
        loading={isLoading}
      />
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Notification Preferences
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Choose what notifications you want to receive.
        </p>
      </div>

      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Email Notifications
        </h4>
        <div className="space-y-4">
          {[
            {
              key: 'video_complete',
              label: 'Video Generation Complete',
              desc: 'When your AI-generated videos are ready'
            },
            {
              key: 'upload_success',
              label: 'YouTube Upload Success',
              desc: 'When videos are successfully uploaded to YouTube'
            },
            {
              key: 'weekly_report',
              label: 'Weekly Performance Report',
              desc: 'Weekly analytics and performance summaries',
              pro: true
            },
            {
              key: 'security_alerts',
              label: 'Security Alerts',
              desc: 'Login attempts and security-related notifications'
            },
            {
              key: 'feature_updates',
              label: 'Feature Updates',
              desc: 'New features and product announcements'
            },
          ].map((notification) => (
            <div key={notification.key} className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h5 className="font-medium text-gray-900 dark:text-white">
                    {notification.label}
                  </h5>
                  {notification.pro && !hasFeature('analytics') && (
                    <Crown className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {notification.desc}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  defaultChecked={!notification.pro || hasFeature('analytics')}
                  disabled={notification.pro && !hasFeature('analytics')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
              </label>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Push Notifications
        </h4>
        <div className="space-y-4">
          {[
            { key: 'browser_notifications', label: 'Browser Notifications', desc: 'Show notifications in your browser' },
            { key: 'desktop_notifications', label: 'Desktop Notifications', desc: 'Show notifications on your desktop' },
          ].map((notification) => (
            <div key={notification.key} className="flex items-center justify-between">
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white">
                  {notification.label}
                </h5>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {notification.desc}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Appearance
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Customize how TubeOps looks and feels.
        </p>
      </div>

      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Theme
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { id: 'light', name: 'Light', desc: 'Clean and minimal' },
            { id: 'dark', name: 'Dark', desc: 'Easy on the eyes' },
            { id: 'system', name: 'System', desc: 'Match your device' },
          ].map((themeOption) => (
            <label key={themeOption.id} className="relative cursor-pointer">
              <input
                type="radio"
                name="theme"
                value={themeOption.id}
                checked={theme === themeOption.id}
                onChange={toggleTheme}
                className="sr-only peer"
              />
              <Card className="p-4 text-center border-2 border-transparent peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20 hover:border-primary-300 transition-colors">
                <div className="w-8 h-8 mx-auto mb-2 rounded bg-gray-200 dark:bg-gray-700"></div>
                <h5 className="font-medium text-gray-900 dark:text-white">{themeOption.name}</h5>
                <p className="text-sm text-gray-600 dark:text-gray-400">{themeOption.desc}</p>
              </Card>
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Interface Preferences
        </h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white">
                Compact Mode
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Show more content on screen
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white">
                Sidebar Always Open
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Keep navigation sidebar expanded
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderBillingTab = () => {
    if (subscription?.tier === 'free') {
      return (
        <Card className="p-6">
          <div className="text-center py-8">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Free Plan
            </h4>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Upgrade to Pro to unlock premium features
            </p>
            <Button onClick={() => navigate('/upgrade')}>
              Upgrade Now
            </Button>
          </div>
        </Card>
      );
    }

    if (billingLoading) {
      return (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              Loading billing information...
            </span>
          </div>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {/* Current Subscription */}
        <Card className="p-6">
          <h4 className="font-medium text-gray-900 dark:text-white mb-4">
            Current Subscription
          </h4>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Plan</span>
              <span className="font-medium text-gray-900 dark:text-white capitalize">
                {subscriptionDetails?.tier || subscription?.tier}
              </span>
            </div>

            {subscriptionDetails?.billing_interval && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Billing</span>
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {subscriptionDetails.billing_interval}ly
                </span>
              </div>
            )}

            {subscriptionDetails?.status && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <span className={`font-medium capitalize ${subscriptionDetails.status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                  {subscriptionDetails.status}
                </span>
              </div>
            )}

            {subscriptionDetails?.next_billing_date && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Next billing date</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatDate(subscriptionDetails.next_billing_date)}
                </span>
              </div>
            )}

            {subscriptionDetails?.cancel_at_period_end && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Your subscription will be cancelled at the end of the current billing period.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReactivateSubscription}
                  className="mt-2"
                >
                  Reactivate Subscription
                </Button>
              </div>
            )}
          </div>

          {!subscriptionDetails?.cancel_at_period_end && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/upgrade')}
              >
                Change Plan
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="text-red-600 hover:text-red-700"
              >
                {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
              </Button>
            </div>
          )}
        </Card>

        {/* Payment Method */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900 dark:text-white">
              Payment Method
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdatePaymentMethod}
              disabled={updatingPayment}
            >
              {updatingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update'
              )}
            </Button>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {subscriptionDetails?.latest_invoice?.payment_method_brand
                  ? `${subscriptionDetails.latest_invoice.payment_method_brand.toUpperCase()} •••• ${subscriptionDetails.latest_invoice.payment_method_last4}`
                  : 'Card on file'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {subscriptionDetails?.latest_invoice?.payment_method_exp_month && subscriptionDetails?.latest_invoice?.payment_method_exp_year
                  ? `Expires ${subscriptionDetails.latest_invoice.payment_method_exp_month}/${subscriptionDetails.latest_invoice.payment_method_exp_year}`
                  : 'Payment method active'}
              </p>
            </div>
          </div>
        </Card>

        {/* Billing History */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900 dark:text-white">
              Billing History
            </h4>
          </div>

          {billingHistory.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No billing history yet
            </p>
          ) : (
            <div className="space-y-3">
              {billingHistory.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatAmount(payment.amount, payment.currency)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(payment.date)}
                    </p>
                    {payment.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {payment.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex items-center space-x-2">
                    <span className={`text-sm font-medium ${payment.status === 'paid'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                      }`}>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                    {payment.invoice_pdf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(payment.invoice_pdf, '_blank')}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        PDF
                      </Button>
                    )}
                    {payment.hosted_invoice_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(payment.hosted_invoice_url, '_blank')}
                        className="text-xs"
                      >
                        View
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  const renderDataTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Data & Privacy
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Control your data and privacy settings.
        </p>
      </div>

      {/* Data Export */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              Export Your Data
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Download a copy of all your data including projects, videos, and settings.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowExportDialog(true)}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export Data
          </Button>
        </div>
      </Card>

      {/* Privacy Settings */}
      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Privacy Settings
        </h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white">
                Analytics Tracking
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Help improve TubeOps by sharing anonymous usage data
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900 dark:text-white">
                Performance Monitoring
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Allow us to monitor app performance to fix bugs faster
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </Card>

      {/* Data Retention */}
      <Card className="p-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
          Data Retention
        </h4>
        <div className="space-y-4">
          <div>
            <h5 className="font-medium text-gray-900 dark:text-white mb-2">
              Automatic Cleanup
            </h5>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Automatically delete old projects and generated content to save storage space.
            </p>

            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input type="radio" name="cleanup" value="never" className="mr-2" />
                <span className="text-sm">Never</span>
              </label>
              <label className="flex items-center">
                <input type="radio" name="cleanup" value="30days" className="mr-2" defaultChecked />
                <span className="text-sm">30 days</span>
              </label>
              <label className="flex items-center">
                <input type="radio" name="cleanup" value="90days" className="mr-2" />
                <span className="text-sm">90 days</span>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Export Data Modal */}
      <ConfirmDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onConfirm={handleExportData}
        title="Export Your Data"
        message="This will create a downloadable archive of all your data. You'll receive an email when it's ready (usually within a few minutes)."
        confirmLabel="Start Export"
        loading={isLoading}
      />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`/settings/${tab.id}`);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'account' && renderAccountTab()}
            {activeTab === 'notifications' && renderNotificationsTab()}
            {activeTab === 'appearance' && renderAppearanceTab()}
            {activeTab === 'billing' && renderBillingTab()}
            {activeTab === 'data' && renderDataTab()}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;